import { useMemo } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mk, type MutationVars } from "~/state/mutations";
import {
  GUEST,
  generateId,
  useAlertsData,
  useCompletionsData,
  useOwnerId,
  useTasksData,
  writeGuest,
  type Task,
  type TaskAlert,
  type TaskCompletion,
} from "~/state/sync";
import type { TaskAlertKind } from "~/api/types";
import {
  addDaysIso,
  currentStreak,
  expectedOccurrences,
  todayIso,
} from "~/utils/cadence";

export type TaskStats = {
  taskId: string;
  completed30: number;
  expected30: number;
  rate30: number;
  completed90: number;
  expected90: number;
  rate90: number;
  streak: number;
  sparkline: number[];
};

function rate(completed: number, expected: number) {
  if (expected <= 0) return 0;
  return Math.min(completed / expected, 1);
}

export function statsForTask(
  task: Task,
  completions: TaskCompletion[],
  today = todayIso(),
): TaskStats {
  const dates = completions
    .filter((row) => row.task_id === task.id && !row.deleted)
    .map((row) => row.completed_on);
  const window30 = addDaysIso(today, -29);
  const window90 = addDaysIso(today, -89);
  const completed30 = dates.filter(
    (day) => day >= window30 && day <= today,
  ).length;
  const completed90 = dates.filter(
    (day) => day >= window90 && day <= today,
  ).length;
  const expected30 = expectedOccurrences(task.cadence, window30, today);
  const expected90 = expectedOccurrences(task.cadence, window90, today);
  const sparkline = Array.from({ length: 14 }, (_, index) => {
    const day = addDaysIso(today, index - 13);
    return dates.includes(day) ? 1 : 0;
  });

  return {
    taskId: task.id,
    completed30,
    expected30,
    rate30: rate(completed30, expected30),
    completed90,
    expected90,
    rate90: rate(completed90, expected90),
    streak: currentStreak(dates, task.cadence, today),
    sparkline,
  };
}

export function useTaskStats(taskId: string | undefined) {
  const { data: tasks } = useTasksData();
  const { data: completions } = useCompletionsData();
  return useMemo(() => {
    if (!taskId) return null;
    const task = (tasks ?? []).find((t) => t.id === taskId);
    if (!task) return null;
    return statsForTask(task, completions ?? []);
  }, [tasks, completions, taskId]);
}

export function useListAnalytics() {
  const { data: tasks } = useTasksData();
  const { data: completions } = useCompletionsData();
  return useMemo(() => {
    const today = todayIso();
    const rows = tasks ?? [];
    const done = completions ?? [];
    const perTask = rows.map((task) => statsForTask(task, done, today));
    const completed30 = perTask.reduce(
      (sum, stats) => sum + stats.completed30,
      0,
    );
    const expected30 = perTask.reduce(
      (sum, stats) => sum + stats.expected30,
      0,
    );
    const longestStreak = perTask.reduce(
      (max, stats) => Math.max(max, stats.streak),
      0,
    );
    return {
      today,
      taskCount: rows.length,
      completed30,
      expected30,
      rate30: rate(completed30, expected30),
      longestStreak,
      perTask,
    };
  }, [tasks, completions]);
}

export function useAlerts() {
  const { data } = useAlertsData();
  return data ?? [];
}

export function useAlertActions() {
  const userId = useOwnerId();
  const queryClient = useQueryClient();
  const { data: alerts } = useAlertsData();
  const scope = userId ?? GUEST;

  const createM = useMutation<unknown, Error, MutationVars["alertCreate"]>({
    mutationKey: [...mk.alertCreate],
  });
  const updateM = useMutation<unknown, Error, MutationVars["alertUpdate"]>({
    mutationKey: [...mk.alertUpdate],
  });

  function upsert(kind: TaskAlertKind, hourLocal = 18, enabled = true) {
    const existing = (alerts ?? []).find(
      (alert) => alert.kind === kind && !alert.list_id && !alert.task_id,
    );
    if (existing) {
      const fields: Partial<TaskAlert> = {
        hour_local: hourLocal,
        enabled,
      };
      if (userId) {
        updateM.mutate({ scope, id: existing.id, fields });
      } else {
        writeGuest<TaskAlert>(queryClient, "task_alerts", (list) =>
          list.map((a) => (a.id === existing.id ? { ...a, ...fields } : a)),
        );
      }
      return existing.id;
    }
    const id = generateId();
    const now = new Date().toISOString();
    const row: TaskAlert = {
      id,
      owner_id: userId ?? GUEST,
      list_id: null,
      task_id: null,
      kind,
      hour_local: hourLocal,
      enabled,
      created_at: now,
      updated_at: now,
      deleted: false,
    };
    if (userId) {
      createM.mutate({ scope, row });
    } else {
      writeGuest<TaskAlert>(queryClient, "task_alerts", (list) => [
        ...list,
        row,
      ]);
    }
    return id;
  }

  return { upsert };
}
